import {
  deleteProfile,
  deleteUser,
  editProfile,
  editUser,
  getAccess,
  getAllUsers,
  getPermissions,
  getProfileById,
  getProfileList,
  getProfilLookup,
  getProfilTransfer,
  getUserById,
  getUserConnectedDetail,
  getUserInfo,
  saveProfil,
  saveUser,
} from "@core/actions/usersActions.ts";
import { FetchStatusType } from "@core/modeles/model.ts";
import { RootState } from "@core/store/store.ts";
import {
  Permission,
  ProfilDTO,
  ProfilTransferDTO,
} from "@modules/setting/userManagement/profil.model.ts";
import { UserDTO } from "@modules/setting/userManagement/user.model.ts";
import { createSlice, PayloadAction } from "@reduxjs/toolkit";
import { PaginateResponse } from "@utils/appUtils.ts";
import { KeycloakProfile } from "keycloak-js";
 
interface UsersState {
  usersLists: PaginateResponse<UserDTO> | null;
  loadingUsers: FetchStatusType;
  userListFiltered: UserDTO[];
  userDetails: UserDTO | null;
  userConnected: UserDTO | null;
  loadingUserDetails: FetchStatusType;
  loadingDataUser: FetchStatusType;
  loadingPermission: FetchStatusType;
  loadingAccess: FetchStatusType;
  loadingSaveProfile: FetchStatusType;
  loadingProfile: FetchStatusType;
  loadingProfileTransfer: FetchStatusType;
  loadingEditProfil: FetchStatusType;
  loadingDeleteUser: FetchStatusType;
  loadingDeleteProfile: FetchStatusType;
  loadingGetProfileId: FetchStatusType;
  profileKeycloak: KeycloakProfile | undefined;
  userInfoKeycloak: KeycloakProfile | undefined;
  dataUser: any | null;
  userRole: string[];
  saveLoader: FetchStatusType;
  loadingEditUser: FetchStatusType;
  permission: Permission[];
  access: Permission[];
  profileById: ProfilDTO;
  profileList: ProfilDTO[];
  profileResponse: PaginateResponse<ProfilDTO> | null;
  profilTransfer: ProfilTransferDTO[];
  profilLookup: ProfilTransferDTO[];
  selectedAccess: number[],
  selectedPermission: number[],
}
 
const initialState: UsersState = {
  usersLists: null,
  loadingUsers: FetchStatusType.IDLE,
  userListFiltered: [],
  userDetails: null,
  userConnected: null,
  loadingUserDetails: FetchStatusType.IDLE,
  loadingDataUser: FetchStatusType.IDLE,
  loadingProfile: FetchStatusType.IDLE,
  loadingPermission: FetchStatusType.IDLE,
  loadingAccess: FetchStatusType.IDLE,
  loadingSaveProfile: FetchStatusType.IDLE,
  loadingEditProfil: FetchStatusType.IDLE,
  loadingEditUser: FetchStatusType.IDLE,
  loadingDeleteUser: FetchStatusType.IDLE,
  saveLoader: FetchStatusType.IDLE,
  loadingProfileTransfer: FetchStatusType.IDLE,
  loadingDeleteProfile: FetchStatusType.IDLE,
  loadingGetProfileId: FetchStatusType.IDLE,
  selectedAccess: [],
  selectedPermission: [],
  profileKeycloak: undefined,
  userInfoKeycloak: undefined,
  userRole: [],
  dataUser: null,
  profileList: [],
  permission: [],
  access: [],
  profileById: {} as ProfilDTO,
  profileResponse: null,
  profilTransfer: [],
  profilLookup: [],
};
 
const UsersSlice = createSlice({
  name: "users",
  initialState,
  reducers: {
    setUsersLists(state, action: PayloadAction<PaginateResponse<UserDTO>>) {
      return { ...state, usersLists: action.payload };
    },
    setUsersListsFiltered(state, action: PayloadAction<UserDTO[]>) {
      return { ...state, userListFiltered: action.payload };
    },
    setProfileKeycloak(state, action: PayloadAction<KeycloakProfile>) {
      return { ...state, profileKeycloak: action.payload };
    },
    setUserInfoKeycloak(state, action: PayloadAction<KeycloakProfile>) {
      return { ...state, userInfoKeycloak: action.payload };
    },
    setUserRole(state, action: PayloadAction<string[]>) {
      return { ...state, userRole: action.payload };
    },
    setSaveLoader(state, action: PayloadAction<FetchStatusType>) {
      return { ...state, saveLoader: action.payload };
    },
    setProfileList(state, action: PayloadAction<[]>) {
      return { ...state, profileList: action.payload };
    },
    setUserDetails(state, action: PayloadAction<UserDTO | null>) {
      return { ...state, userDetails: action.payload };
    },
    setUserConnectedDetails(state, action: PayloadAction<UserDTO | null>) {
      return { ...state, userConnected: action.payload };
    },
    setPermission(state, action: PayloadAction<Permission[]>) {
      return { ...state, permission: action.payload };
    },
    setAccess(state, action: PayloadAction<Permission[]>) {
      return { ...state, access: action.payload };
    },
    setSelectedAccess(state, action: PayloadAction<number[]>) {
      return { ...state, selectedAccess: action.payload };
    },
    setSelectedPermission(state, action: PayloadAction<number[]>) {
      return { ...state, selectedPermission: action.payload };
    },
  },
  extraReducers: (builder) => {
    // lists user
    builder
      .addCase(getAllUsers.pending, (state) => {
        return { ...state, loadingUsers: FetchStatusType.LOADING };
      })
      .addCase(getAllUsers.fulfilled, (state, action) => {
        return {
          ...state,
          loadingUsers: FetchStatusType.COMPLETED,
          usersLists: action.payload,
          userListFiltered: action.payload.data,
        };
      })
      .addCase(getAllUsers.rejected, (state) => {
        return { ...state, loadingUsers: FetchStatusType.FAILED };
      });
 
    // details user
    builder
      .addCase(getUserById.pending, (state) => {
        return { ...state, loadingUserDetails: FetchStatusType.LOADING };
      })
      .addCase(getUserById.fulfilled, (state, action) => {
        return {
          ...state,
          loadingUserDetails: FetchStatusType.COMPLETED,
          userDetails: action.payload,
        };
      })
      .addCase(getUserById.rejected, (state) => {
        return { ...state, loadingUserDetails: FetchStatusType.FAILED };
      });
 
    // infos user
    builder
      .addCase(getUserInfo.pending, (state) => {
        return { ...state, loadingDataUser: FetchStatusType.LOADING };
      })
      .addCase(getUserInfo.fulfilled, (state, action) => {
        return {
          ...state,
          loadingDataUser: FetchStatusType.COMPLETED,
          dataUser: action.payload,
        };
      })
      .addCase(getUserInfo.rejected, (state) => {
        return { ...state, loadingDataUser: FetchStatusType.FAILED };
      });
 
    // save user
    builder
      .addCase(saveUser.pending, (state) => {
        return { ...state, saveLoader: FetchStatusType.LOADING };
      })
      .addCase(saveUser.fulfilled, (state) => {
        return {
          ...state,
          saveLoader: FetchStatusType.COMPLETED,
        };
      })
      .addCase(saveUser.rejected, (state) => {
        return { ...state, saveLoader: FetchStatusType.FAILED };
      });
 
    // get profil list
    builder
      .addCase(getProfileList.pending, (state) => {
        return { ...state, loadingProfile: FetchStatusType.LOADING };
      })
      .addCase(getProfileList.rejected, (state) => {
        return { ...state, loadingProfile: FetchStatusType.FAILED };
      })
      .addCase(getProfileList.fulfilled, (state, action) => {
        return {
          ...state,
          loadingProfile: FetchStatusType.COMPLETED,
          profileList: action.payload.data,
          profileResponse: action.payload,
        };
      });
 
    builder
      .addCase(getProfilTransfer.pending, (state) => {
        return { ...state, loadingProfileTransfer: FetchStatusType.LOADING };
      })
      .addCase(getProfilTransfer.rejected, (state) => {
        return { ...state, loadingProfileTransfer: FetchStatusType.FAILED };
      })
      .addCase(getProfilTransfer.fulfilled, (state, action) => {
        return {
          ...state,
          loadingProfileTransfer: FetchStatusType.COMPLETED,
          profilTransfer: action.payload,
        };
      });

    builder.addCase(getProfilLookup.fulfilled, (state, action) => {
      return { ...state, profilLookup: action.payload };
    });
 
    // get permission list
    builder
      .addCase(getPermissions.pending, (state) => {
        return { ...state, loadingPermission: FetchStatusType.LOADING };
      })
      .addCase(getPermissions.fulfilled, (state, action) => {
        return {
          ...state,
          loadingPermission: FetchStatusType.COMPLETED,
          permission: action.payload,
        };
      })
      .addCase(getPermissions.rejected, (state) => {
        return { ...state, loadingPermission: FetchStatusType.FAILED };
      });
 
    // get access list
    builder
      .addCase(getAccess.pending, (state) => {
        return { ...state, loadingAccess: FetchStatusType.LOADING };
      })
      .addCase(getAccess.fulfilled, (state, action) => {
        return {
          ...state,
          loadingAccess: FetchStatusType.COMPLETED,
          access: action.payload,
        };
      })
      .addCase(getAccess.rejected, (state) => {
        return { ...state, loadingAccess: FetchStatusType.FAILED };
      });
 
    // edit user
    builder
      .addCase(editUser.pending, (state) => {
        return { ...state, loadingEditUser: FetchStatusType.LOADING };
      })
      .addCase(editUser.fulfilled, (state) => {
        return {
          ...state,
          loadingEditUser: FetchStatusType.COMPLETED,
        };
      })
      .addCase(editUser.rejected, (state) => {
        return { ...state, loadingEditUser: FetchStatusType.FAILED };
      });
 
    // delete user
    builder
      .addCase(deleteUser.pending, (state) => {
        return { ...state, loadingDeleteUser: FetchStatusType.LOADING };
      })
      .addCase(deleteUser.fulfilled, (state) => {
        return {
          ...state,
          loadingDeleteUser: FetchStatusType.COMPLETED,
        };
      })
      .addCase(deleteUser.rejected, (state) => {
        return { ...state, loadingDeleteUser: FetchStatusType.FAILED };
      });
 
    // details user connected
    builder
      .addCase(getUserConnectedDetail.pending, (state) => {
        return { ...state, loadingUserDetails: FetchStatusType.LOADING };
      })
      .addCase(getUserConnectedDetail.fulfilled, (state, action) => {
        return {
          ...state,
          loadingUserDetails: FetchStatusType.COMPLETED,
          userConnected: action.payload,
        };
      })
      .addCase(getUserConnectedDetail.rejected, (state) => {
        return { ...state, loadingUserDetails: FetchStatusType.FAILED };
      });
 
    // save profile
    builder
      .addCase(saveProfil.pending, (state) => {
        return { ...state, loadingSaveProfile: FetchStatusType.LOADING };
      })
      .addCase(saveProfil.fulfilled, (state, action) => {
        return {
          ...state,
          loadingSaveProfile: FetchStatusType.COMPLETED,
        };
      })
      .addCase(saveProfil.rejected, (state) => {
        return { ...state, loadingSaveProfile: FetchStatusType.FAILED };
      });
 
    // edit profile
    builder
      .addCase(editProfile.pending, (state) => {
        return { ...state, loadingEditProfil: FetchStatusType.LOADING };
      })
      .addCase(editProfile.fulfilled, (state, action) => {
        return {
          ...state,
          loadingEditProfil: FetchStatusType.COMPLETED,
        };
      })
      .addCase(editProfile.rejected, (state) => {
        return { ...state, loadingEditProfil: FetchStatusType.FAILED };
      });
 
    // delete profile
    builder
      .addCase(deleteProfile.pending, (state) => {
        return { ...state, loadingDeleteProfile: FetchStatusType.LOADING };
      })
      .addCase(deleteProfile.fulfilled, (state) => {
        return {
          ...state,
          loadingDeleteProfile: FetchStatusType.COMPLETED,
        };
      })
      .addCase(deleteProfile.rejected, (state) => {
        return { ...state, loadingDeleteProfile: FetchStatusType.FAILED };
      });
 
    // get profile by Id
    builder
      .addCase(getProfileById.pending, (state) => {
        return { ...state, loadingGetProfileId: FetchStatusType.LOADING };
      })
      .addCase(getProfileById.fulfilled, (state, action) => {
        return {
          ...state,
          loadingGetProfileId: FetchStatusType.COMPLETED,
          profileById: action.payload,
        };
      })
      .addCase(getProfileById.rejected, (state) => {
        return { ...state, loadingGetProfileId: FetchStatusType.FAILED };
      });
  },
});
 
export const {
  setUsersLists,
  setUsersListsFiltered,
  setProfileKeycloak,
  setUserInfoKeycloak,
  setUserRole,
  setSaveLoader,
  setProfileList,
  setUserDetails,
  setUserConnectedDetails,
  setPermission,
  setAccess,
  setSelectedAccess,
  setSelectedPermission,
} = UsersSlice.actions;
 
export const userLists = (state: RootState) => state.user.usersLists;
 
export const loadingUser = (state: RootState) => state.user.loadingUsers;
 
export const userFiltered = (state: RootState) => state.user.userListFiltered;
 
export const loadingUserDetails = (state: RootState) =>
  state.user.loadingUserDetails;
 
export const userDetails = (state: RootState) => state.user.userDetails;
 
export const profileKeycloak = (state: RootState) => state.user.profileKeycloak;
 
export const dataUser = (state: RootState) => state.user.dataUser;
 
export const userInfoKeycloak = (state: RootState) =>
  state.user.userInfoKeycloak;
 
export const userRole = (state: RootState) => state.user.userRole;
 
export const saveLoader = (state: RootState) => state.user.saveLoader;
 
export const profileList = (state: RootState) => state.user.profileList;
 
export const loadingEditUser = (state: RootState) => state.user.loadingEditUser;
 
export const loadingDeleteUser = (state: RootState) =>
  state.user.loadingDeleteUser;
 
export const userConnected = (state: RootState) => state.user.userConnected;
 
export const permission = (state: RootState) => state.user.permission;
 
export const access = (state: RootState) => state.user.access;
 
export const loadingSaveProfile = (state: RootState) =>
  state.user.loadingSaveProfile;
 
export const loadingPermission = (state: RootState) =>
  state.user.loadingPermission;
 
export const loadingAccess = (state: RootState) => state.user.loadingAccess;
 
export const loadingProfile = (state: RootState) => state.user.loadingProfile;
 
export const loadingEditProfil = (state: RootState) =>
  state.user.loadingEditProfil;
 
export const loadingDeleteProfile = (state: RootState) =>
  state.user.loadingDeleteProfile;
 
export const loadingGetProfileId = (state: RootState) =>
  state.user.loadingGetProfileId;
 
export const profileById = (state: RootState) => state.user.profileById;
 
export const profilResponse = (state: RootState) => state.user.profileResponse;
 
export const profilTransfer = (state: RootState) => state.user.profilTransfer;
export const profilLookup = (state: RootState) => state.user.profilLookup;

export const selectedaccess = (state: RootState) => state.user.selectedAccess;

export const selectedpermission = (state: RootState) => state.user.selectedPermission;

 
export default UsersSlice.reducer;